import React, {useState} from "react";
import {Navigate, Link as RouterLink} from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {Modal} from "react-bootstrap";
import Privacy from "../privacy";
import {auth} from "../../firebase";
import {useUserAuth} from "../../context/UserAuthContext";
import {
    addAssociationRecord,
    nowFormatted,
    uploadAssociationPhoto,
    friendlyErrorMessage,
    DEFAULT_ASSOCIATION_LOGO,
} from "../../services/associationService";
import {CheckCircleOutline} from "@mui/icons-material";

const PRIMARY = "#3E4F88";

/**
 * Registo de associações no browser.
 *
 * Fluxo igual à app móvel:
 *   1. Criar conta Firebase (email + password)
 *   2. Enviar email de verificação
 *   3. Carregar a foto para Association/{NIF}.jpg
 *   4. Criar o registo em "Association" com Enabled=false (um admin ativa depois)
 */
const RegisterPage = () => {

    const {user, role, signUp, verifyEmail} = useUserAuth();

    // Campos
    const [name, setName] = useState("");
    const [nif, setNif] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordAgain, setPasswordAgain] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [linkedin, setLinkedin] = useState("");
    const [info, setInfo] = useState("");
    const [terms, setTerms] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [done, setDone] = useState(false);
    // false = o registo correu mas o email de verificação não foi enviado
    const [emailSent, setEmailSent] = useState(true);
    const [showPolicy, setShowPolicy] = useState(false);

    // Já autenticado e sem registo em curso? Não precisa de se registar.
    // (O flag "registering" evita o redirecionamento prematuro logo após
    //  o signUp, enquanto a foto e o registo RTDB ainda estão a ser criados.)
    if (user && !done && !registering) {
        if (role === "association") {
            return <Navigate to="/app/associacao" replace/>;
        }
        return <Navigate to="/login" replace/>;
    }

    const onPhotoChange = (e) => {
        const file = e.target.files && e.target.files[0];
        setPhoto(file || null);
        if (file) {
            setPhotoPreview(URL.createObjectURL(file));
        } else {
            setPhotoPreview(null);
        }
    };

    const validate = () => {
        if (!name.trim()) return "Indica o nome da associação.";
        if (!nif.trim()) return "Indica o NIF da associação.";
        if (!email.trim()) return "Indica o email.";
        if (!password) return "Indica a palavra-passe.";
        if (password.length < 6) return "A palavra-passe deve ter pelo menos 6 caracteres.";
        if (password !== passwordAgain) return "As palavras-passe não coincidem.";
        if (!photo) return "Carrega a foto/logo da associação.";
        if (!terms) return "Aceita os termos para continuar.";
        return null;
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");

        const problem = validate();
        if (problem) {
            setError(problem);
            return;
        }

        setLoading(true);
        setRegistering(true);
        try {
            // 1) Criar conta Firebase (erros específicos tratados no catch final)
            const cred = await signUp(email.trim(), password);

            // 2) Email de verificação (igual à app).
            //    Usamos o user retornado pelo signUp (garantido não nulo) em vez de
            //    auth.currentUser, que pode ainda não estar definido.
            //    Se falhar, o registo continua: o email pode ser reenviado a partir
            //    da vista de login ("Reenviar email de verificação").
            let emailOk = true;
            try {
                await verifyEmail(cred.user);
            } catch (err) {
                console.error("Falha ao enviar o email de verificação (o registo continua):", err);
                emailOk = false;
            }

            // 3) Carregar a foto (mesmo caminho da app: Association/{NIF}.jpg).
            //    Se o upload falhar (ex.: storage do Firebase indisponível), usamos o
            //    logo por omissão para não bloquear o registo — a foto pode ser
            //    alterada depois em "O meu perfil".
            let profileUrl;
            try {
                profileUrl = await uploadAssociationPhoto(nif.trim(), photo);
            } catch (err) {
                console.error("Falha no upload da foto; a usar o logo por omissão.", err);
                profileUrl = DEFAULT_ASSOCIATION_LOGO;
            }

            // 4) Registo "Association" (Enabled=false até um admin ativar)
            const record = {
                Type: "association",
                Id: cred.user.uid,
                Email: email.trim(),
                ProfileURL: profileUrl,
                Name: name.trim(),
                Address: address.trim(),
                PhoneNumber: phone.trim(),
                NIF: nif.trim(),
                OtherInfo: info.trim(),
                Linkedin: linkedin.trim(),
                RegisterDate: nowFormatted(),
                Dicas: "",
                Associationtoken: "",
                Enabled: false,
                EmailVerified: false,
            };
            await addAssociationRecord(record);

            setEmailSent(emailOk);
            setDone(true);
        } catch (err) {
            const specific = friendlyErrorMessage(err);
            // A mensagem (err.message) tem a descrição real (ex.: de um TypeError);
            // o code vem depois. Antes mostrávamos só o nome ("TypeError") sem a descrição.
            const detail = (err && (err.message || err.code || err.name)) || "desconhecido";
            console.error("Erro no registo:", err);
            if (specific) {
                setError(specific);
            } else {
                setError(
                    `O registo não foi concluído (erro: ${detail}). ` +
                    "Se a conta de email foi criada, confirma o email e tenta terminar o registo novamente, " +
                    "ou contacta esf@centro.oep.pt."
                );
            }
        } finally {
            setLoading(false);
            // Nota: em caso de falha o "registering" mantém-se true de propósito,
            // para não redirecionar o utilizador antes de ver a mensagem de erro.
        }
    };

    // ---------- vista de sucesso ----------
    if (done) {
        return (
            <Container maxWidth="sm" sx={{py: 8}}>
                <Stack spacing={3} alignItems="center" sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 5}}>
                    <CheckCircleOutline sx={{fontSize: 56, color: "#2e7d32"}}/>
                    <Typography variant="h5" sx={{fontWeight: 700}}>Registo efetuado com sucesso</Typography>
                    <Alert severity="success" sx={{alignItems: "flex-start"}}>
                        {emailSent ? (
                            <>
                                Confirma o teu email: chegou-te um email de verificação para <b>{email.trim()}</b>.
                                Depois de verificar o email, um administrador da plataforma irá ativar a tua conta.
                            </>
                        ) : (
                            <>
                                O registo foi criado, mas não foi possível enviar o email de verificação de momento.
                                Entra na plataforma ("Ir para o login") e usa "Reenviar email de verificação"
                                quando tiveres oportunidade. Depois de verificar o email, um administrador
                                da plataforma irá ativar a tua conta.
                            </>
                        )}
                    </Alert>
                    <Typography sx={{textAlign: "center", fontWeight: 600}}>
                        Após a ativação, inicia sessão para apresentares o desafio da associação.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
                        Não recebeste o email? Verifica a pasta de spam.
                        Em caso de dúvidas, contacta <b>esf@centro.oep.pt</b>.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to="/login"
                        variant="contained"
                        size="large"
                        sx={{bgcolor: PRIMARY, fontWeight: 600}}
                    >
                        Ir para o login
                    </Button>
                </Stack>
            </Container>
        );
    }

    // ---------- formulário ----------
    return (
        <Box component="main" sx={{bgcolor: "#f4f5fa", py: 4}}>
            <Container maxWidth="md">
                <Stack spacing={1} sx={{mb: 3}}>
                    <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                        Registo de Associação
                    </Typography>
                    <Typography color="text.secondary">
                        Cria a conta da tua associação na plataforma E4IU.
                        Os campos assinalados com * são obrigatórios.
                    </Typography>
                </Stack>

                <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 4}}>
                    <Button
                        size="small"
                        component={RouterLink}
                        to="/"
                        sx={{textTransform: "none", alignSelf: "flex-start", color: "text.secondary", mb: 1}}
                    >
                        ← Voltar à página inicial
                    </Button>
                    <form onSubmit={submit} noValidate>
                        <Stack spacing={2}>
                            <Alert severity="info" sx={{alignItems: "flex-start"}}>
                                Depois de a conta ser ativada, apresentam um <b>desafio</b> — a
                                necessidade técnica da vossa associação que precisam de resolver,
                                em qualquer área da engenharia (civil, elétrica, florestal,
                                informática, entre outras). Os nossos voluntários vão ajudá-los
                                a encontrar a melhor solução.
                            </Alert>
                            {error && <Alert severity="error">{error}</Alert>}

                            {/* Foto / logo */}
                            <Stack direction={{xs: "column", sm: "row"}} spacing={3} alignItems="center">
                                <Avatar
                                    src={photoPreview || undefined}
                                    sx={{width: 72, height: 72, border: 2, borderColor: photo ? "success.main" : "error.main"}}
                                >
                                    🏛️
                                </Avatar>
                                <Stack spacing={1} sx={{flex: 1, alignItems: "stretch"}}>
                                    <Typography variant="body2" fontWeight={600}>
                                        Foto / logo da associação *
                                    </Typography>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        sx={{color: PRIMARY, borderColor: PRIMARY, alignSelf: "flex-start"}}
                                    >
                                        Escolher imagem
                                        <input type="file" accept="image/*" hidden onChange={onPhotoChange}/>
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        JPG/PNG. Será usada como o logótipo da associação.
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Nome da associação"
                                    size="small"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    sx={{flex: 2}}
                                />
                                <TextField
                                    label="NIF"
                                    size="small"
                                    required
                                    value={nif}
                                    onChange={(e) => setNif(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    size="small"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    sx={{flex: 2}}
                                />
                                <TextField
                                    label="Telefone"
                                    size="small"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Palavra-passe"
                                    type="password"
                                    size="small"
                                    required
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    sx={{flex: 1}}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography variant="caption" color="text.secondary" component="span">
                                                    mín. 6 caracteres
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Confirmar palavra-passe"
                                    type="password"
                                    size="small"
                                    required
                                    autoComplete="new-password"
                                    value={passwordAgain}
                                    onChange={(e) => setPasswordAgain(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                <TextField
                                    label="Morada"
                                    size="small"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    sx={{flex: 2}}
                                />
                                <TextField
                                    label="LinkedIn"
                                    size="small"
                                    value={linkedin}
                                    onChange={(e) => setLinkedin(e.target.value)}
                                    sx={{flex: 1}}
                                />
                            </Stack>

                            <TextField
                                label="Outras informações"
                                size="small"
                                multiline
                                minRows={2}
                                value={info}
                                onChange={(e) => setInfo(e.target.value)}
                                helperText="O que a vossa associação faz? (opcional)"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={terms}
                                        onChange={(e) => setTerms(e.target.checked)}
                                        sx={{color: PRIMARY, "&.Mui-checked": {color: PRIMARY}}}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{display: "inline"}}>
                                        Aceito os termos de utilização e a{" "}
                                        <Button
                                            size="small"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPolicy(true);
                                            }}
                                            sx={{
                                                textTransform: "none",
                                                color: PRIMARY,
                                                fontWeight: 600,
                                                p: 0,
                                                minWidth: "auto",
                                                lineHeight: 1.6,
                                                textDecoration: "underline",
                                            }}
                                        >
                                            política de privacidade
                                        </Button>{" "}
                                        da plataforma. *
                                    </Typography>
                                }
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{bgcolor: PRIMARY, fontWeight: 600, alignSelf: "flex-start"}}
                            >
                                {loading ? "A registar…" : "Registar"}
                            </Button>
                        </Stack>
                    </form>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                    Já tens conta?{" "}
                    <RouterLink to="/login" style={{color: PRIMARY, fontWeight: 600}}>
                        Inicia sessão
                    </RouterLink>
                    {" · "}
                    <RouterLink to="/registo/engenheiro" style={{color: PRIMARY, fontWeight: 600}}>
                        Registo de voluntário/a
                    </RouterLink>
                </Typography>
            </Container>

            {/* Política de privacidade em modal (mesmo conteúdo da página /privacy) —
                o formulário mantém os dados preenchidos ao fechar */}
            <Modal show={showPolicy} onHide={() => setShowPolicy(false)} size="lg">
                <Modal.Header closeButton/>
                <Modal.Body>
                    <Privacy/>
                </Modal.Body>
            </Modal>
        </Box>
    );
};

export default RegisterPage;